import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { BrushStart } from "../../../models/chart_model";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { H_CHART_TYPES } from "../../Hppc/const";
import { formatDateForAPI } from "../const";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { getStateAvailability } from "../Utility/RestApi";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";

let Intervals = [
  {
    label: "Time Slot",
    key: "slot",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

const TYPE_OPTIONS = [
  "Total",
  "KEB Complex",
  "DSPMTPS Complex",
  "KWB Ph I+II",
  "KWB U5",
  "ABVTPS",
  "Hydel",
  "Solar Injection",
  "Open Access Injection",
  "Small Hydel Injection",
  "Biomass Injection",
  "REMC",
  "ISGS",
  "LTA",
];

export function getMonthWithYear(dateString: string): string {
  const date = new Date(dateString);
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  return `${month}-${year}`;
}

export function ChhattisgarhAvailability() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [data, setData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<string[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);

  const chartSuffixes = [
    { suffix: "total", displayName: "Total", stackSuffix: "total" },
    { suffix: "keb_complex", displayName: "KEB Complex", stackSuffix: "keb_complex" },
    { suffix: "dspmtps_complex", displayName: "DSPMTPS Complex", stackSuffix: "dspmtps_complex" },
    { suffix: "kwb_ph_i_plus_ii", displayName: "KWB Ph I+II", stackSuffix: "kwb_ph_i_plus_ii" },
    { suffix: "kwb_u_5", displayName: "KWB U5", stackSuffix: "kwb_u_5" },
    { suffix: "abvtps", displayName: "ABVTPS", stackSuffix: "abvtps" },
    { suffix: "hydel", displayName: "Hydel", stackSuffix: "hydel" },
    { suffix: "solar_injection", displayName: "Solar Injection", stackSuffix: "solar_injection" },
    { suffix: "open_access_injection", displayName: "Open Access Injection", stackSuffix: "open_access_injection" },
    { suffix: "small_hydel_injection", displayName: "Small Hydel Injection", stackSuffix: "small_hydel_injection" },
    { suffix: "biomass_injection", displayName: "Biomass Injection", stackSuffix: "biomass_injection" },
    { suffix: "total_remc", displayName: "REMC", stackSuffix: "total_remc" },
    { suffix: "total_isgs", displayName: "ISGS", stackSuffix: "total_isgs" },
    { suffix: "total_lta", displayName: "LTA", stackSuffix: "total_lta" },
  ];

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedInterval: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string },
    ExchangeColors: string[],
  ) => {
    return {
      dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
      legendColor: undefined,
      type: selectedChartType.value,
      stackId: selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  const [selectedSuffix, setSelectedSuffix] = useState(chartSuffixes);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) return;
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDateStr = formatDateForAPI(dates.startDate);
    const endDateStr = formatDateForAPI(dates.endDate);

    try {
      const stateAvailData = await getStateAvailability(startDateStr, endDateStr);

      const newData = stateAvailData.map((item: any) => {
        const totalMw =
          (item.keb_complex || 0) +
          (item.dspmtps_complex || 0) +
          (item.kwb_ph_i_plus_ii || 0) +
          (item.kwb_u_5 || 0) +
          (item.abvtps || 0) +
          (item.hydel || 0) +
          (item.solar_injection || 0) +
          (item.open_access_injection || 0) +
          (item.small_hydel_injection || 0) +
          (item.biomass_injection || 0) +
          (item.total_remc || 0) +
          (item.total_isgs || 0) +
          (item.total_lta || 0);
        return {
        ...item,
        block: item.slot ?? item.block,
        month: getMonthWithYear(item.date),
        total_mw: totalMw,
        total_mwh: totalMw / 4,
        total_lu: totalMw / 400,
        keb_complex_mw: item.keb_complex || 0,
        keb_complex_mwh: (item.keb_complex || 0) / 4,
        keb_complex_lu: (item.keb_complex || 0) / 400,
        dspmtps_complex_mw: item.dspmtps_complex || 0,
        dspmtps_complex_mwh: (item.dspmtps_complex || 0) / 4,
        dspmtps_complex_lu: (item.dspmtps_complex || 0) / 400,
        kwb_ph_i_plus_ii_mw: item.kwb_ph_i_plus_ii || 0,
        kwb_ph_i_plus_ii_mwh: (item.kwb_ph_i_plus_ii || 0) / 4,
        kwb_ph_i_plus_ii_lu: (item.kwb_ph_i_plus_ii || 0) / 400,
        kwb_u_5_mw: item.kwb_u_5 || 0,
        kwb_u_5_mwh: (item.kwb_u_5 || 0) / 4,
        kwb_u_5_lu: (item.kwb_u_5 || 0) / 400,
        abvtps_mw: item.abvtps || 0,
        abvtps_mwh: (item.abvtps || 0) / 4,
        abvtps_lu: (item.abvtps || 0) / 400,
        hydel_mw: item.hydel || 0,
        hydel_mwh: (item.hydel || 0) / 4,
        hydel_lu: (item.hydel || 0) / 400,
        solar_injection_mw: item.solar_injection || 0,
        solar_injection_mwh: (item.solar_injection || 0) / 4,
        solar_injection_lu: (item.solar_injection || 0) / 400,
        open_access_injection_mw: item.open_access_injection || 0,
        open_access_injection_mwh: (item.open_access_injection || 0) / 4,
        open_access_injection_lu: (item.open_access_injection || 0) / 400,
        small_hydel_injection_mw: item.small_hydel_injection || 0,
        small_hydel_injection_mwh: (item.small_hydel_injection || 0) / 4,
        small_hydel_injection_lu: (item.small_hydel_injection || 0) / 400,
        biomass_injection_mw: item.biomass_injection || 0,
        biomass_injection_mwh: (item.biomass_injection || 0) / 4,
        biomass_injection_lu: (item.biomass_injection || 0) / 400,
        total_remc_mw: item.total_remc || 0,
        total_remc_mwh: (item.total_remc || 0) / 4,
        total_remc_lu: (item.total_remc || 0) / 400,
        total_isgs_mw: item.total_isgs || 0,
        total_isgs_mwh: (item.total_isgs || 0) / 4,
        total_isgs_lu: (item.total_isgs || 0) / 400,
        total_lta_mw: item.total_lta || 0,
        total_lta_mwh: (item.total_lta || 0) / 4,
        total_lta_lu: (item.total_lta || 0) / 400,
      };
    });

      setData(newData);
    } catch (error) {
      console.error("Error fetching state availability data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    let filteredData = data.map((item) => ({
      ...item,
      month: getMonthWithYear(item.date),
    }));

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((b) => b.value).includes(item.block),
      );
    }

    filteredData.sort((a, b) => {
      const dateCompare = new Date(a.date).getTime() - new Date(b.date).getTime();
      if (dateCompare !== 0) return dateCompare;
      return a.block - b.block;
    });

    function getKeys() {
      if (selectedInterval.key === "slot") {
        return ["date", "block"];
      } else if (selectedInterval.key === "date") {
        return ["date"];
      } else {
        return [selectedInterval.values[0]];
      }
    }

    const par: ChartDataParams = {
      keys: getKeys(),
      groupBy: [],
      tableKeys: ["date", "block", "month"],
      volumeKey: ``,
      aggregationKeys: [
        "total_mw", "total_mwh", "total_lu",
        "keb_complex_mw", "keb_complex_mwh", "keb_complex_lu",
        "dspmtps_complex_mw", "dspmtps_complex_mwh", "dspmtps_complex_lu",
        "kwb_ph_i_plus_ii_mw", "kwb_ph_i_plus_ii_mwh", "kwb_ph_i_plus_ii_lu",
        "kwb_u_5_mw", "kwb_u_5_mwh", "kwb_u_5_lu",
        "abvtps_mw", "abvtps_mwh", "abvtps_lu",
        "hydel_mw", "hydel_mwh", "hydel_lu",
        "solar_injection_mw", "solar_injection_mwh", "solar_injection_lu",
        "open_access_injection_mw", "open_access_injection_mwh", "open_access_injection_lu",
        "small_hydel_injection_mw", "small_hydel_injection_mwh", "small_hydel_injection_lu",
        "biomass_injection_mw", "biomass_injection_mwh", "biomass_injection_lu",
        "total_remc_mw", "total_remc_mwh", "total_remc_lu",
        "total_isgs_mw", "total_isgs_mwh", "total_isgs_lu",
        "total_lta_mw", "total_lta_mwh", "total_lta_lu",
      ],
      priceKey: "",
      averageKeys: [
        "total_mw", "total_mwh", "total_lu",
        "keb_complex_mw", "keb_complex_mwh", "keb_complex_lu",
        "dspmtps_complex_mw", "dspmtps_complex_mwh", "dspmtps_complex_lu",
        "kwb_ph_i_plus_ii_mw", "kwb_ph_i_plus_ii_mwh", "kwb_ph_i_plus_ii_lu",
        "kwb_u_5_mw", "kwb_u_5_mwh", "kwb_u_5_lu",
        "abvtps_mw", "abvtps_mwh", "abvtps_lu",
        "hydel_mw", "hydel_mwh", "hydel_lu",
        "solar_injection_mw", "solar_injection_mwh", "solar_injection_lu",
        "open_access_injection_mw", "open_access_injection_mwh", "open_access_injection_lu",
        "small_hydel_injection_mw", "small_hydel_injection_mwh", "small_hydel_injection_lu",
        "biomass_injection_mw", "biomass_injection_mwh", "biomass_injection_lu",
        "total_remc_mw", "total_remc_mwh", "total_remc_lu",
        "total_isgs_mw", "total_isgs_mwh", "total_isgs_lu",
        "total_lta_mw", "total_lta_mwh", "total_lta_lu",
      ],
      averageDuplicationKeys: ["date", "block", "month"],
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [data, selectedInterval, selectedBlock, selectedSuffix, selectedUnit]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function handleDiscomChange(items: string[]) {
    setSelectedDiscom(items);
  }

  function handleTypeChange(selected: string[]) {
    const newSuffix = selected
      .map((s) => chartSuffixes.find((cs) => cs.displayName === s))
      .filter(Boolean) as typeof chartSuffixes;
    setSelectedSuffix(newSuffix.length > 0 ? newSuffix : [chartSuffixes[0]]);
  }

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.isTime) {
      return "block";
    } else {
      return selectedInterval.key;
    }
  }, [selectedInterval.isTime, selectedInterval.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  function secondDataKey() {
    return selectedInterval.isTime ? "date" : undefined;
  }

  const summaryExtraKeys = selectedInterval.isTime
    ? ["block", selectedInterval.values[1]]
    : [selectedInterval.values[0]];

  const summaryExtraLabels = selectedInterval.values.map((value) =>
    value === "date" ? "Date" : value,
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(getLegends());

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    block_key: "block",
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "CSPDCL State Availability",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  // if (selectedXAxisMode.value === "peak") {
  //   return (
  //     <ChhattisgarhPeakAnalysis
  //       onXAxisModeChange={handleXAxisModeChange}
  //       selectedUnit={selectedUnit}
  //       onUnitChange={handleUnitChange}
  //       selectedDiscom={selectedDiscom}
  //       onDiscomChange={handleDiscomChange}
  //       selectedGroupKeys=""
  //       onGroupByChange={() => {}}
  //       selectedSuffix={selectedSuffix}
  //       onTypeChange={handleTypeChange}
  //       chartSuffixes={chartSuffixes}
  //       metrics={null}
  //     />
  //   );
  // }

  // if (selectedXAxisMode.value === "summary") {
  //   return (
  //     <ChhattisgarhSummaryAnalysis
  //       onXAxisModeChange={handleXAxisModeChange}
  //       selectedUnit={selectedUnit}
  //       onUnitChange={handleUnitChange}
  //       selectedDiscom={selectedDiscom}
  //       onDiscomChange={handleDiscomChange}
  //       selectedGroupKeys=""
  //       onGroupByChange={() => {}}
  //       selectedSuffix={selectedSuffix}
  //       onTypeChange={handleTypeChange}
  //       chartSuffixes={chartSuffixes}
  //       metrics={null}
  //     />
  //   );
  // }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Intervals"
            items={Intervals.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => {
              setSelectedInterval(Intervals[index]);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              handleUnitChange(item);
            }}
          />
          <FilterMenu
            title="Type"
            items={TYPE_OPTIONS}
            selectedItems={selectedSuffix.map((s) => s.displayName)}
            setSelectedItems={handleTypeChange}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading State Availability"
              subtitle="Fetching Chhattisgarh state availability data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-60">
                  State Availability ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                      dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                      dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 30 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedInterval.isTime}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={getLegends()}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={xDataKey}
                  xTick={
                    selectedInterval.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => renderHourTick(value, 10)
                      : undefined
                  }
                  secondXDataKey={secondDataKey()}
                />
              )}
              {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>CSPDCL</p>}
              text="1. State Availability data is sourced from CSPDCL's official records."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );

  function getLegends() {
    return selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
      chartKeys.map(
        (key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            selectedInterval,
            selectedChartType as any,
            ExchangeColors,
          ) as any,
      ),
    );
  }
}
